// src/components/Home/Hero.js

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectCreative, Parallax } from "swiper/modules";
import { motion } from "framer-motion";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-creative";
import "swiper/css/parallax";
import "./styles/hero.css";

import defaultHeroBgImage from "../../assets/img/slider1.jpg";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api/";

const useDeviceDetect = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

const Hero = () => {
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useDeviceDetect();

  useEffect(() => {
    const bannerEndpoint = isMobile ? "mobile-banner" : "banner";
    const fetchBanners = async () => {
      try {
        const response = await axios.get(`${API_URL}${bannerEndpoint}`);
        if (response.data.success && response.data.data.length > 0) {
          const activeBanners = response.data.data.filter((b) => b.isActive);
          setBanners(activeBanners);
        }
      } catch (err) {
        console.error(
          `Error fetching ${isMobile ? "mobile" : "PC"} banners:`,
          err
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanners();
  }, [isMobile]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const renderSlideContent = (banner, slideIndex) => {
    const hasContent = banner.title || banner.description || banner.buttonText;

    return (
      <>
        {/* Decorative elements */}
        <div className="hero-decorative-elements">
          <div className="hero-circle hero-circle-1"></div>
          <div className="hero-circle hero-circle-2"></div>
          <div className="hero-circle hero-circle-3"></div>
        </div>

        {hasContent && (
          <motion.div
            key={`content-${slideIndex}`}
            variants={containerVariants}
            initial="hidden"
            animate={activeIndex === slideIndex ? "visible" : "hidden"}
            className="slider_text"
          >
            {/* Glass morphism card wrapper */}
            <div className="hero-content-card">
              {banner.title && (
                <motion.div variants={itemVariants} className="hero-title-wrapper">
                  <h1 className="hero-title">
                    {banner.title}
                  </h1>
                  <div className="hero-title-underline"></div>
                </motion.div>
              )}

              {banner.description && (
                <motion.p
                  variants={itemVariants}
                  className="subtitle"
                >
                  {banner.description}
                </motion.p>
              )}

              {banner.buttonText && (
                <motion.div
                  variants={itemVariants}
                  className="shop_btn_wrapper"
                >
                  <button className="Shop_Now_hero">
                    <span className="button-text">{banner.buttonText}</span>
                    <span className="button-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                  <div className="button-glow"></div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </>
    );
  };

  const fallbackBanner = {
    title: "Welcome to Booze247",
    description: "Your favorite drinks, delivered fast, anytime.",
    image: defaultHeroBgImage,
    link: "/shop",
    buttonText: "Shop Now",
    isLocal: true,
  };

  if (isLoading) {
    return (
      <div className="hero-container-section loading-state">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
      </div>
    );
  }

  const displayBanners = banners.length > 0 ? banners : [fallbackBanner];

  return (
    <section className="hero-section">
      <Swiper
        modules={[Autoplay, Pagination, Navigation, EffectCreative, Parallax]}
        effect="creative"
        creativeEffect={{
          prev: {
            shadow: true,
            translate: ["-20%", 0, -1],
            opacity: 0,
          },
          next: {
            translate: ["100%", 0, 0],
            opacity: 0,
          },
        }}
        parallax={true}
        speed={1200}
        spaceBetween={0}
        slidesPerView={1}
        loop={displayBanners.length > 1}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
          dynamicMainBullets: 3,
        }}
        navigation={displayBanners.length > 1}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="hero-swiper"
      >
        {displayBanners.map((banner, index) => (
          <SwiperSlide key={banner._id || index}>
            <Link to={banner.link || "/shop"} className="hero-slide-link">
              <div className="hero-container-section">
                {/* Background image with parallax */}
                <div
                  className="hero-bg-image"
                  data-swiper-parallax="-23%"
                  style={{
                    backgroundImage: `url(${banner.isLocal
                      ? banner.image
                      : `${API_URL}uploads/${banner.image}`
                      })`,
                  }}
                />

                {/* Content */}
                <div className="hero-content-wrapper" data-swiper-parallax="-100">
                  {renderSlideContent(banner, index)}
                </div>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Scroll indicator */}
      <div className="hero-scroll-indicator">
        <div className="scroll-mouse">
          <div className="scroll-wheel"></div>
        </div>
        <span className="scroll-text">Scroll to explore</span>
      </div>
    </section>
  );
};

export default Hero;

